import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Activity } from 'lucide-react';
import { useTicketThread } from '@/hooks/useTicketThread';
import { ThreadEventList } from '../ThreadEventList';
import { ScrollArea } from '@/components/ui/scroll-area';

interface WorkflowEventsSectionProps {
  ticketId: string;
}

type FilterType = 'all' | 'status' | 'timer' | 'workflow' | 'moves';

const FILTER_EVENT_TYPES: Record<FilterType, string[] | null> = {
  all: null,
  status: ['ticket_created', 'ticket_assigned', 'ticket_reassigned', 'ticket_status_changed', 'ticket_completed'],
  timer: ['ticket_started', 'ticket_paused', 'ticket_resumed', 'timer_stopped'],
  workflow: [
    'workflow_report_initialized', 'workflow_complaint_added', 'workflow_complaint_updated',
    'workflow_note_added', 'workflow_note_updated',
    'workflow_inspection_started', 'workflow_inspection_saved', 'workflow_inspection_completed',
    'workflow_diagnostic_created', 'workflow_diagnostic_updated', 'workflow_diagnostic_started', 'workflow_diagnostic_completed',
    'service_info_acknowledgment', 'service_info_acknowledgment_updated',
    'job_time_edited',
  ],
  moves: ['ticket_moved_to_parking_lot', 'ticket_moved_from_parking_lot', 'ticket_bay_changed', 'ticket_date_changed', 'ticket_reassigned'],
};

export function WorkflowEventsSection({ ticketId }: WorkflowEventsSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const { events, isLoading } = useTicketThread(ticketId);

  const filteredEvents = FILTER_EVENT_TYPES[filter]
    ? events.filter(e => FILTER_EVENT_TYPES[filter]!.includes(e.event_type))
    : events;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <Activity className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Ticket Events</span>
            <Badge variant="secondary" className="text-xs">{filteredEvents.length}</Badge>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pt-2 pb-4 space-y-3">
          {/* Filter chips */}
          <div className="flex gap-1.5 flex-wrap">
            {(['all', 'status', 'timer', 'workflow', 'moves'] as FilterType[]).map(f => (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? 'default' : 'outline'}
                className="h-7 text-xs capitalize"
                onClick={() => setFilter(f)}
              >
                {f}
              </Button>
            ))}
          </div>
          <ScrollArea className="h-[400px] overflow-y-auto pr-2">
            <ThreadEventList events={filteredEvents} isLoading={isLoading} />
          </ScrollArea>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
